import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { apartmentInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '#galerija', label: t.nav.gallery },
    { href: '#o-apartmanu', label: t.nav.about },
    { href: '#lokacija', label: t.nav.location },
    { href: '#atrakcije', label: t.nav.attractions },
    { href: '#faq', label: t.nav.faq },
    { href: '#kontakt', label: t.nav.contact },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleLanguageChange = (lang) => {
    // First change language, then close menu with slight delay to avoid race condition
    setLanguage(lang);
    setTimeout(() => {
      setIsLangMenuOpen(false);
    }, 50);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href={language === 'hr' ? '/hr' : '/'}
            className={`font-serif text-lg md:text-xl font-bold transition-colors duration-300 ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            <span className="block leading-tight">{apartmentInfo.name}</span>
            <span className="text-xs font-normal opacity-80">{apartmentInfo.subtitle}</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className={`text-sm font-medium transition-colors duration-300 hover:opacity-80 whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-amber-700' : 'text-white/90 hover:text-white'
                }`}
              >
                {item.label}
              </a>
            ))}
            
            {/* Language Switcher - Desktop */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 px-2 py-1 rounded ${
                  isScrolled 
                    ? 'text-gray-700 hover:text-amber-700 hover:bg-gray-100' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
                data-testid="language-switcher"
              >
                <Globe size={16} />
                <span>{language.toUpperCase()}</span>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLanguageChange('en');
                    }}
                    data-testid="language-option-en"
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-amber-50 transition-colors ${
                      language === 'en' ? 'bg-amber-100 text-amber-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    🇬🇧 English
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLanguageChange('hr');
                    }}
                    data-testid="language-option-hr"
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-amber-50 transition-colors ${
                      language === 'hr' ? 'bg-amber-100 text-amber-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    🇧🇦 Bosanski
                  </button>
                </div>
              )}
            </div>

            <a
              href="#rezervacija"
              onClick={(e) => scrollToSection(e, '#rezervacija')}
              className="bg-amber-700 hover:bg-amber-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t.nav.reserve}
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            {/* Language Switcher - Mobile */}
            <button
              onClick={() => handleLanguageChange(language === 'en' ? 'hr' : 'en')}
              className={`p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <Globe size={20} />
            </button>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 bg-white rounded-xl shadow-xl">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                >
                  {item.label}
                </a>
              ))}
              
              {/* Language options in mobile menu */}
              <div className="px-4 py-2 border-t border-gray-100 mt-2">
                <p className="text-xs text-gray-500 mb-2">Language / Jezik</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      language === 'en' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🇬🇧 EN
                  </button>
                  <button
                    onClick={() => handleLanguageChange('hr')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      language === 'hr' 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🇧🇦 HR
                  </button>
                </div>
              </div>
              
              <div className="px-4 pt-2">
                <a
                  href="#rezervacija"
                  onClick={(e) => scrollToSection(e, '#rezervacija')}
                  className="block w-full bg-amber-700 hover:bg-amber-800 text-white px-5 py-3 rounded-lg text-center font-medium transition-all duration-300"
                >
                  {t.nav.reserve}
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
